import { Injectable, Optional } from "@angular/core";
import { AuthConfig } from "./auth.config";
import { OAuthStorage } from "./types";
import { HttpHeaders } from "@angular/common/http";
import { LocalStorageKey, UserInfo } from "@royalty/model";

@Injectable()
export class OAuthService extends AuthConfig {
	protected _storage: OAuthStorage;

	constructor(@Optional() storage: OAuthStorage) {
		super();

		try {
			if (storage) {
				this.setStorage(storage);
			} else if (typeof sessionStorage !== "undefined") {
				this.setStorage(sessionStorage);
			}
		} catch (e) {
			console.error(
				"No OAuthStorage provided and cannot access default (sessionStorage)." +
				"Consider providing a custom OAuthStorage implementation in your module.",
				e
			);
		}
	}

	public setStorage(storage: OAuthStorage): void {
		this._storage = storage;
		this.configChanged();
	}

	protected configChanged(): void { }

	public storeAccessTokenResponse(
		accessToken: string,
		refreshToken: string,
		expiresIn: number,
		tokenType: string
	): void {
		this._storage.setItem(LocalStorageKey.access_token, accessToken);
		this._storage.setItem(LocalStorageKey.tokenType, tokenType);
		this._storage.setItem(
			LocalStorageKey.access_token_stored_at,
			"" + Date.now()
		);
		if (expiresIn) {
			const expiresInMilliSeconds = expiresIn * 1000;
			const now = new Date();
			const expiresAt = now.getTime() + expiresInMilliSeconds;
			this._storage.setItem(LocalStorageKey.expires_at, "" + expiresAt);
		}

		if (refreshToken) {
			this._storage.setItem(LocalStorageKey.refresh_token, refreshToken);
		}
	}

	public storePermissions(perms: string): void {
		this._storage.setItem(LocalStorageKey.X_PERM, perms);
	}

	public storeDefaultLanguage(language: string): void {
		this._storage.setItem(LocalStorageKey.language, language);
	}

	public storeTimeAutoLogOff(secondTime: string): void {
		this._storage.setItem(LocalStorageKey.logOff, secondTime);
	}

	public storeUserInfo(userInfo: UserInfo): void {
		this._storage.setItem(
			LocalStorageKey.user_info,
			JSON.stringify(userInfo)
		);
	}

	public getAuthorizationHeader(): HttpHeaders {
		// Creates header for the auth requests.
		let headers: HttpHeaders = new HttpHeaders().set(
			"Content-Type",
			"application/json"
		);
		headers = headers.append("Accept", "application/json");
		headers = headers.append("Access-Control-Allow-Origin", "*");

		headers = headers.append(
			"Access-Control-Allow-Headers",
			"Content-Type"
		);

		const language: string = this.getDefaultLanguage();
		headers = headers.append("Accept-Language", language);

		const desktop: string = this.getCurrentDevice();
		if (desktop) {
			headers = headers.append("X-DEVICE-TYPE", desktop);
		}

		const token: string = this.getAccessToken();
		if (token) {
			const tokenValue: string = "Bearer " + token;
			headers = headers.append("Authorization", tokenValue);
		}
		return headers;
	}

	/**
	 * Checkes, whether there is a valid access_token.
	 */
	public hasValidAccessToken(): boolean {
		if (this.getAccessToken()) {
			// TODO: Need handle with refresh token
			const expiresAt = this._storage.getItem(LocalStorageKey.expires_at);
			const now = new Date();
			if (expiresAt && parseInt(expiresAt, 10) < now.getTime()) {
				return false;
			}
			return true;
		}

		return false;
	}

	/**
	 * Checkes, whether there is a existing access_token.
	 */
	public existingToken(): boolean {
		if (this.getAccessToken()) {
			return true;
		}

		return false;
	}

	/**
	 * Returns table ids opening.
	 */
	public getUserInfo(): UserInfo {
		const user = this._storage.getItem(LocalStorageKey.user_info);
		if (!user) {
			return null;
		}
		return JSON.parse(user);
	}

	/**
	 * Returns table ids opening.
	 */
	public getTableIdsOpening(): number[] {
		const tbls = this._storage.getItem(LocalStorageKey.tbls);
		if (!tbls) {
			return null;
		}
		return JSON.parse(tbls);
	}

	/**
	 * Remove table ids opening.
	 */
	public removeTableIdsOpening(): void {
		this._storage.removeItem(LocalStorageKey.tbls);
	}

	/**
	 * Returns the received claims about the user.
	 */
	public getIdentityClaims(): object {
		const claims = this._storage.getItem(
			LocalStorageKey.id_token_claims_obj
		);
		if (!claims) {
			return null;
		}
		return JSON.parse(claims);
	}

	/**
	 * Returns the current permissions.
	 */
	public getPermissions(): string {
		// return localStorage.getItem(LocalStorageKey.X_PERM);
		return this._storage.getItem(LocalStorageKey.X_PERM);
	}

	/**
	 * Returns the current access_token.
	 */
	public getAccessToken(): string {
		return this._storage.getItem(LocalStorageKey.access_token);
	}

	/**
	 * Returns the current language using.
	 */
	public getDefaultLanguage(): string {
		return this._storage.getItem(LocalStorageKey.language) || "vi";
	}

	/**
	 * Returns the current device using.
	 */
	public getCurrentDevice(): string {
		return (
			this._storage.getItem(LocalStorageKey.X_DEVICE_TYPE) || "DESKTOP"
		);
	}

	/**
	 * Returns the current access_token.
	 */

	public getRefreshToken(): string {
		return this._storage.getItem(LocalStorageKey.refresh_token);
	}

	/**
	 * Returns the expiration date of the access_token
	 * as milliseconds since 1970.
	 */
	public getAccessTokenExpiration(): number {
		if (!this._storage.getItem(LocalStorageKey.expires_at)) {
			return null;
		}
		return parseInt(this._storage.getItem(LocalStorageKey.expires_at), 10);
	}

	protected getAccessTokenStoredAt(): number {
		return parseInt(
			this._storage.getItem(LocalStorageKey.access_token_stored_at),
			10
		);
	}

	protected getIdTokenStoredAt(): number {
		return parseInt(
			this._storage.getItem(LocalStorageKey.id_token_stored_at),
			10
		);
	}

	public logOut(): void {
		this._storage.removeItem(LocalStorageKey.user_info);
		this._storage.removeItem(LocalStorageKey.access_token);
		this._storage.removeItem(LocalStorageKey.id_token);
		this._storage.removeItem(LocalStorageKey.refresh_token);
		this._storage.removeItem(LocalStorageKey.tokenType);
		this._storage.removeItem(LocalStorageKey.expires_at);
		this._storage.removeItem(LocalStorageKey.id_token_claims_obj);
		this._storage.removeItem(LocalStorageKey.id_token_stored_at);
		this._storage.removeItem(LocalStorageKey.access_token_stored_at);
		this._storage.removeItem(LocalStorageKey.X_PERM);
		this._storage.removeItem(LocalStorageKey.table_detail);
		this._storage.removeItem(LocalStorageKey.tbls);
		this._storage.setItem(LocalStorageKey.language, "vi");
	}

	/**
	 * Returns the current id_token.
	 */
	public getIdToken(): string {
		return this._storage
			? this._storage.getItem(LocalStorageKey.id_token)
			: null;
	}

	public refreshToken(): Promise<object> {
		// Todo
		return new Promise((resolve, reject) => { });
	}
}
